/**
 * Serviço de Análise de IA para Comunicações Jurídicas
 * Utiliza OpenAI GPT-4o mini para análise inteligente de textos
 */

class AIAnalysisService {
  constructor() {
    // Configuração da OpenAI
    this.apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    this.apiUrl = 'https://api.openai.com/v1/chat/completions';
    this.model = 'gpt-4o-mini';
  }

  /**
   * Analisar comunicação jurídica com IA
   */
  async analyzeComunicacao(comunicacao) {
    try {
      console.log('🤖 Iniciando análise de IA para comunicação:', comunicacao.id);

      const prompt = this.buildAnalysisPrompt(comunicacao);
      const response = await this.callOpenAI(prompt);
      
      const analysis = this.parseAIResponse(response);
      console.log('✅ Análise de IA concluída:', analysis);
      
      return analysis;
    } catch (error) {
      console.error('❌ Erro na análise de IA:', error);
      return this.getFallbackAnalysis(comunicacao);
    }
  }

  /**
   * Construir prompt OTIMIZADO para análise jurídica
   * Baseado em especialista em direito processual
   */
  buildAnalysisPrompt(comunicacao) {
    const dataDisponibilizacao = comunicacao.data_disponibilizacao;
    const hoje = new Date().toISOString().split('T')[0];
    const tribunal = comunicacao.siglaTribunal || 'N/A';

    return `
Você é um assistente jurídico especializado em análise de comunicações processuais do PJe e cálculo confiável de prazos.

COMUNICAÇÃO PARA ANÁLISE:
- Tribunal: ${tribunal} - ${comunicacao.nomeOrgao || 'N/A'}
- Processo: ${comunicacao.numeroprocessocommascara || comunicacao.numero_processo || 'N/A'}
- Data da Comunicação/Disponibilização: ${dataDisponibilizacao}
- Data Atual: ${hoje}
- Texto: ${comunicacao.texto}

TAREFA:
A partir do texto, você deve:
1. Detectar prazos explícitos e implícitos
2. Identificar a data-base correta do ato (e o tipo de ato)
3. Calcular a data exata de início e fim do prazo conforme o rito/justiça aplicável
4. Explicar como calculou (rastro de auditoria)
5. Marcar incertezas sem deixar de devolver as melhores estimativas

REGRAS DE DETECÇÃO DE PRAZOS:
- PRAZOS EXPLÍCITOS: "15 dias para contestar", "prazo de 5 dias", "48 horas", "no prazo de 10 (dez) dias úteis"
- PRAZOS IMPLÍCITOS: Citação (prazo de contestação conforme rito), intimações para manifestação/embargos/agravos
- EXPRESSÕES DE SUSPENSÃO: "torno sem efeito", "suspenso", "prorrogo", "reabro prazo" → ajustar conforme texto
- UNIDADES: reconhecer dias úteis x dias corridos x horas

REGRAS DE CONTAGEM POR RAMO:
- PROCESSO CIVIL (CPC/Justiça Comum): dias úteis (art. 219 CPC). Excluir dia do começo, incluir do vencimento
- TRABALHO (CLT): dias úteis (art. 775 CLT, Lei 13.467/17)
- PENAL (CPP): dias corridos (art. 798 CPP), salvo disposição específica
- JUIZADOS ESPECIAIS: dias corridos (Lei 9.099/95), salvo previsão expressa contrária

PROCESSOS ELETRÔNICOS (Lei 11.419/2006):
- Intimação por portal: realizada no dia do acesso pelo advogado. Se sem acesso em 10 dias corridos, considera-se realizada ao término desses 10 dias
- Publicação no DJe: data da publicação é o dia útil seguinte à disponibilização; prazo começa no primeiro dia útil seguinte à publicação
- Prazo encerra às 23:59 do dia do vencimento

IDENTIFICAR DATA-BASE:
- Portal PJe: se houve acesso → usar data do acesso; se sem acesso → usar data automática do 10º dia corrido
- DJe: usar data da publicação (dia útil seguinte à disponibilização)
- Audiência/ato presencial: usar data do ato
- Mandado/citação: data certificada/AR

Retorne APENAS um JSON válido:

{
  "numeroProcesso": "string",
  "cliente": "string|null",
  "temPrazo": true,
  "tipoAto": "citacao|intimacao|publicacaoDJe|audiencia|outro",
  "dataComunicacao": "${dataDisponibilizacao}",
  "origemComunicacao": "portal|dje|mandado|audiencia|outro",
  "prazos": [
    {
      "descricao": "Contestação",
      "quantidade": 15,
      "unidade": "dias",
      "diasUteis": true,
      "fundamentoLegal": "ex.: CPC arts. 219, 231, 224; Lei 11.419/2006 art. 5º",
      "dataBase": "YYYY-MM-DD",
      "dataInicio": "YYYY-MM-DD",
      "dataFim": "YYYY-MM-DD",
      "precisao": "alto|estimado",
      "feriadosConsiderados": ["nacionais"],
      "observacoes": "ex.: intimação portal sem acesso: realizada no 10º dia",
      "diasRestantes": 0,
      "urgencia": "hoje|esta_semana|proximo_mes"
    }
  ],
  "urgenciaGeral": "alta|media|baixa",
  "acoesSugeridas": ["ex.: conferir acesso no portal", "confirmar feriados locais", "contestar"],
  "resumo": "string curta explicando como calculou",
  "tags": ["prazo","contestacao","cpc"],
  "checagens": {
    "calendarioLocalValidado": false,
    "houveIndisponibilidade": false,
    "nivelConfianca": 0.9,
    "ritoDetectado": "cpc|clt|cpp|juizado|outro"
  }
}

CHECKLIST OBRIGATÓRIO:
✓ Identificou ramo/rito aplicável? (CPC, CLT, CPP, Juizado etc.)
✓ Determinou corretamente a data-base (acesso portal x 10º dia; publicação DJe; audiência; AR)?
✓ Marcou dias úteis x corridos corretamente?
✓ Excluiu dia do começo e incluiu do vencimento; prorrogou se feriado?
✓ Listou fundamentoLegal e explicou o cálculo em resumo?
✓ Indicou se o calendário local foi validado?
✓ Se havia reabertura/suspensão de prazo, refletiu no resultado?

POLÍTICA DE CONFIANÇA:
- NUNCA omita o fundamentoLegal
- Quando houver ambiguidade, calcule a melhor estimativa, sinalize incerteza em precisao
- Se múltiplos prazos, crie múltiplas entradas
- Se ato reabre/suspende prazo, reflita no cálculo

RETORNE APENAS O JSON, SEM EXPLICAÇÕES.
`;
  }

  /**
   * Chamar API da OpenAI
   */
  async callOpenAI(prompt) {
    if (!this.apiKey) {
      throw new Error('Chave da OpenAI não configurada');
    }

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: JSON.stringify({
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'Você é um assistente jurídico especializado em análise de comunicações processuais.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 1000
      })
    });

    if (!response.ok) {
      throw new Error(`Erro na API OpenAI: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  }

  /**
   * Processar resposta da IA
   */
  parseAIResponse(response) {
    try {
      // Limpar resposta e extrair JSON
      const cleanResponse = response.trim();
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);

      if (!jsonMatch) {
        throw new Error('JSON não encontrado na resposta');
      }

      const analysis = JSON.parse(jsonMatch[0]);

      // Processar prazos com cálculos de data
      const prazosProcessados = Array.isArray(analysis.prazos)
        ? analysis.prazos.map(prazo => this.processarPrazo(prazo))
        : [];

      // Validar estrutura com nova estrutura de prazos
      return {
        numeroProcesso: analysis.numeroProcesso || null,
        cliente: analysis.cliente || null,
        temPrazo: Boolean(analysis.temPrazo),
        prazos: prazosProcessados,
        tipoAto: analysis.tipoAto || 'Não identificado',
        urgenciaGeral: analysis.urgenciaGeral || 'proximo_mes',
        acoesSugeridas: Array.isArray(analysis.acoesSugeridas) ? analysis.acoesSugeridas : [],
        resumo: analysis.resumo || 'Análise não disponível',
        tags: Array.isArray(analysis.tags) ? analysis.tags : [],
        analisadoPorIA: true,
        dataAnalise: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Erro ao processar resposta da IA:', error);
      throw error;
    }
  }

  /**
   * Processar e validar dados de prazo individual
   * IMPORTANTE: A IA retorna 'dataFim', não 'dataVencimento'
   */
  processarPrazo(prazo) {
    try {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      // A IA retorna dataFim, mas precisamos de dataVencimento
      // Prioridade: dataFim > dataVencimento > calcular a partir de dataInicio + quantidade
      let dataVencimentoStr = prazo.dataFim || prazo.dataVencimento;
      let dataInicioStr = prazo.dataInicio || prazo.dataBase;
      const dias = prazo.quantidade || prazo.dias || 0;
      
      // Se não temos data de vencimento válida, calcular a partir de dataInicio + dias
      if (!dataVencimentoStr || dataVencimentoStr === 'Invalid Date') {
        if (dataInicioStr && dias > 0) {
          const inicio = new Date(dataInicioStr);
          if (!isNaN(inicio)) {
            inicio.setHours(0, 0, 0, 0);
            const vencimento = new Date(inicio);
            vencimento.setDate(vencimento.getDate() + dias);
            dataVencimentoStr = vencimento.toISOString().split('T')[0];
            console.log(`📅 Calculando vencimento: ${dataInicioStr} + ${dias} dias = ${dataVencimentoStr}`);
          }
        }
      }
      
      // Se ainda não temos data válida, usar data de hoje + dias (fallback)
      if (!dataVencimentoStr || dataVencimentoStr === 'Invalid Date') {
        const fallback = new Date();
        fallback.setDate(fallback.getDate() + (dias || 15)); // Default 15 dias se nada especificado
        dataVencimentoStr = fallback.toISOString().split('T')[0];
        console.warn('⚠️ Usando data de vencimento fallback:', dataVencimentoStr);
      }
      
      // Calcular dias restantes
      const dataVencimento = new Date(dataVencimentoStr);
      dataVencimento.setHours(0, 0, 0, 0);
      const diasRestantes = Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24));

      // Determinar urgência baseada nos dias restantes
      let urgencia = 'proximo_mes';
      if (diasRestantes < 0) urgencia = 'vencido';
      else if (diasRestantes === 0) urgencia = 'hoje';
      else if (diasRestantes <= 1) urgencia = 'amanha';
      else if (diasRestantes <= 7) urgencia = 'esta_semana';

      console.log(`✅ Prazo processado: ${prazo.descricao || 'N/A'} - Vence em ${dataVencimentoStr} (${diasRestantes} dias)`);

      return {
        tipo: prazo.tipo || prazo.descricao || 'manifestação',
        dias: dias,
        diasUteis: Boolean(prazo.diasUteis),
        dataInicio: dataInicioStr || new Date().toISOString().split('T')[0],
        dataVencimento: dataVencimentoStr,
        diasRestantes: diasRestantes,
        urgencia: urgencia,
        descricao: prazo.descricao || 'Prazo processual',
        fundamentoLegal: prazo.fundamentoLegal || null,
        observacoes: prazo.observacoes || null,
        status: 'pendente'
      };
    } catch (error) {
      console.error('❌ Erro ao processar prazo:', error);
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + 15);
      return {
        tipo: 'manifestação',
        dias: 15,
        diasUteis: true,
        dataInicio: new Date().toISOString().split('T')[0],
        dataVencimento: fallbackDate.toISOString().split('T')[0],
        diasRestantes: 15,
        urgencia: 'proximo_mes',
        descricao: 'Prazo não identificado',
        status: 'pendente'
      };
    }
  }

  /**
   * Análise de fallback (sem IA)
   */
  getFallbackAnalysis(comunicacao) {
    console.log('🔄 Usando análise de fallback (sem IA)');

    const texto = comunicacao.texto || '';
    const numeroProcesso = this.extractProcessNumber(comunicacao);

    // Detecção básica de prazos com regex
    const prazosDetectados = this.detectPrazosRegex(texto, comunicacao.data_disponibilizacao);

    return {
      numeroProcesso,
      cliente: this.extractClienteBasico(texto),
      temPrazo: prazosDetectados.length > 0,
      prazos: prazosDetectados,
      tipoAto: this.detectTipoAto(texto),
      urgenciaGeral: this.calculateUrgenciaGeral(prazosDetectados),
      acoesSugeridas: this.getSuggestedActions(texto),
      resumo: 'Análise básica sem IA',
      tags: this.extractBasicTags(texto),
      analisadoPorIA: false,
      dataAnalise: new Date().toISOString()
    };
  }

  /**
   * Extrair cliente básico do texto
   */
  extractClienteBasico(texto) {
    // Tentar encontrar padrões de nome de pessoa/empresa
    const padroes = [
      /(?:requerente|autor|cliente|parte):\s*([A-Z][a-z\s]+)/gi,
      /([A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/g
    ];

    for (const padrao of padroes) {
      const match = texto.match(padrao);
      if (match && match[0]) {
        return match[0].replace(/^(requerente|autor|cliente|parte):\s*/i, '').trim();
      }
    }

    return null;
  }

  /**
   * Extrair número do processo
   */
  extractProcessNumber(comunicacao) {
    // Tentar várias fontes
    const sources = [
      comunicacao.numeroprocessocommascara,
      comunicacao.numero_processo,
      comunicacao.numeroProcesso
    ];

    for (const source of sources) {
      if (source && typeof source === 'string') {
        // Regex para número de processo brasileiro
        const match = source.match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/);
        if (match) return match[0];
      }
    }

    // Tentar extrair do texto
    const texto = comunicacao.texto || '';
    const match = texto.match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/);
    return match ? match[0] : null;
  }

  /**
   * Detectar prazos com regex (fallback)
   */
  detectPrazosRegex(texto, dataDisponibilizacao) {
    const prazos = [];
    const regexPrazos = [
      /(\d+)\s*dias?\s*(?:úteis?)?\s*para\s*(contestar|recorrer|manifestar|impugnar|apresentar)/gi,
      /prazo\s*de\s*(\d+)\s*dias?\s*(?:úteis?)?/gi,
      /no\s*prazo\s*de\s*(\d+)\s*dias?\s*(?:úteis?)?/gi,
      /(\d+)\s*dias?\s*(?:úteis?)?\s*(?:a\s*contar|contados)/gi
    ];

    const dataInicio = new Date(dataDisponibilizacao || new Date());

    regexPrazos.forEach(regex => {
      let match;
      while ((match = regex.exec(texto)) !== null) {
        const dias = parseInt(match[1]);
        const tipo = match[2] || 'manifestação';
        const diasUteis = /úteis?/i.test(match[0]);

        // Calcular data de vencimento
        const dataVencimento = new Date(dataInicio);
        dataVencimento.setDate(dataVencimento.getDate() + dias);

        // Calcular dias restantes
        const hoje = new Date();
        const diasRestantes = Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24));

        // Determinar urgência
        let urgencia = 'proximo_mes';
        if (diasRestantes < 0) urgencia = 'vencido';
        else if (diasRestantes === 0) urgencia = 'hoje';
        else if (diasRestantes <= 7) urgencia = 'esta_semana';

        prazos.push({
          tipo: tipo,
          dias: dias,
          diasUteis: diasUteis,
          dataInicio: dataInicio.toISOString().split('T')[0],
          dataVencimento: dataVencimento.toISOString().split('T')[0],
          diasRestantes: diasRestantes,
          urgencia: urgencia,
          descricao: `${tipo} - ${dias} dias${diasUteis ? ' úteis' : ''}`,
          status: 'pendente'
        });
      }
    });

    return prazos;
  }

  /**
   * Detectar tipo de ato
   */
  detectTipoAto(texto) {
    const tipos = {
      'citação': /citação|citar/gi,
      'intimação': /intimação|intimar/gi,
      'sentença': /sentença|julgar|decidir/gi,
      'despacho': /despacho|determinar/gi,
      'decisão': /decisão|decidir/gi
    };

    for (const [tipo, regex] of Object.entries(tipos)) {
      if (regex.test(texto)) return tipo;
    }

    return 'comunicação';
  }

  /**
   * Calcular urgência geral
   */
  calculateUrgenciaGeral(prazos) {
    if (prazos.length === 0) return 'proximo_mes';

    const menorDiasRestantes = Math.min(...prazos.map(p => p.diasRestantes || 999));

    if (menorDiasRestantes <= 0) return 'hoje';
    if (menorDiasRestantes <= 7) return 'esta_semana';
    return 'proximo_mes';
  }

  /**
   * Sugerir ações
   */
  getSuggestedActions(texto) {
    const acoes = [];
    
    if (/contestar/gi.test(texto)) acoes.push('Preparar contestação');
    if (/recorrer/gi.test(texto)) acoes.push('Avaliar cabimento de recurso');
    if (/manifestar/gi.test(texto)) acoes.push('Elaborar manifestação');
    if (/prazo/gi.test(texto)) acoes.push('Verificar prazo processual');
    
    return acoes.length > 0 ? acoes : ['Analisar comunicação'];
  }

  /**
   * Extrair tags básicas
   */
  extractBasicTags(texto) {
    const tags = [];
    
    if (/prazo/gi.test(texto)) tags.push('prazo');
    if (/urgente/gi.test(texto)) tags.push('urgente');
    if (/citação/gi.test(texto)) tags.push('citação');
    if (/intimação/gi.test(texto)) tags.push('intimação');
    if (/sentença/gi.test(texto)) tags.push('sentença');
    
    return tags;
  }

  /**
   * Analisar múltiplas comunicações em lote
   */
  async analyzeMultipleComunicacoes(comunicacoes, onProgress) {
    const results = [];
    
    for (let i = 0; i < comunicacoes.length; i++) {
      try {
        const analysis = await this.analyzeComunicacao(comunicacoes[i]);
        results.push({
          comunicacaoId: comunicacoes[i].id,
          analysis
        });
        
        if (onProgress) {
          onProgress(i + 1, comunicacoes.length);
        }
        
        // Delay para evitar rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`❌ Erro ao analisar comunicação ${comunicacoes[i].id}:`, error);
        results.push({
          comunicacaoId: comunicacoes[i].id,
          analysis: this.getFallbackAnalysis(comunicacoes[i])
        });
      }
    }
    
    return results;
  }
}

export default new AIAnalysisService();
